import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  PhotoCamera,
  Delete,
  CloudUpload,
  TextFields,
  Image,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
}));

const DeleteOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(0.5),
  right: theme.spacing(0.5),
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  borderRadius: '50%',
}));

interface CompassQuadrantProps {
  quadrant: 'what_happened' | 'what_did_i_do' | 'how_affected_others' | 'what_can_i_do_better';
  title: string;
  helpText: string;
  placeholder: string;
  textValue: string;
  imageUrl?: string;
  onTextChange: (value: string) => void;
  onImageUpload: (file: File) => Promise<void>;
  onImageDelete: () => Promise<void>;
  disabled?: boolean;
  textRows?: number;
}

const CompassQuadrant: React.FC<CompassQuadrantProps> = ({
  quadrant,
  title,
  helpText,
  placeholder,
  textValue,
  imageUrl,
  onTextChange,
  onImageUpload,
  onImageDelete,
  disabled = false,
  textRows = 6,
}) => {
  const [viewMode, setViewMode] = useState<'text' | 'image'>('text');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'text' | 'image'
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      await onImageUpload(file);
      setViewMode('image'); // Switch to image view after successful upload
    } catch (error) {
      setUploadError('Failed to upload image');
      console.error('Image upload error:', error);
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async () => {
    if (deleting) return;
    
    try {
      setDeleting(true);
      await onImageDelete();
      setViewMode('text'); // Switch back to text view after deletion
    } catch (error) {
      console.error('Image delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h6" color="primary">
          {title}
        </Typography>
        
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          disabled={disabled}
        >
          <ToggleButton value="text" aria-label="text mode">
            <TextFields fontSize="small" />
          </ToggleButton>
          <ToggleButton value="image" aria-label="image mode">
            <Image fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {helpText}
      </Typography>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      <Box sx={{ flexGrow: 1 }}>
        {viewMode === 'text' ? (
          // Text Mode
          <TextField
            fullWidth
            multiline
            rows={textRows}
            placeholder={placeholder}
            value={textValue}
            onChange={(e) => onTextChange(e.target.value)}
            variant="outlined"
            disabled={disabled}
            sx={{
              height: '100%',
              '& .MuiOutlinedInput-root': {
                height: '100%',
                alignItems: 'flex-start',
              },
              '& .MuiInputBase-inputMultiline': {
                height: '100% !important',
              },
            }}
          />
        ) : (
          // Image Mode
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {imageUrl ? (
              <ImagePreview sx={{ flexGrow: 1, minHeight: 200 }}>
                <Card>
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={`Image for ${title}`}
                    sx={{ 
                      width: '100%',
                      height: '100%',
                      minHeight: 200,
                      objectFit: 'cover' 
                    }}
                  />
                </Card>
                <DeleteOverlay>
                  <IconButton
                    size="small"
                    onClick={handleDeleteImage}
                    disabled={deleting || disabled}
                    sx={{ color: 'white' }}
                  >
                    {deleting ? <CircularProgress size={16} color="inherit" /> : <Delete fontSize="small" />}
                  </IconButton>
                </DeleteOverlay>
              </ImagePreview>
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  minHeight: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <PhotoCamera color="disabled" sx={{ fontSize: 40 }} />
                <Typography variant="body2" color="text.secondary">
                  No image uploaded
                </Typography>
              </Box>
            )}

            <Button
              component="label"
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
              disabled={uploading || disabled}
              size="small"
              sx={{ mt: 1, alignSelf: 'flex-start' }}
            >
              {imageUrl ? 'Replace Image' : 'Upload Image'}
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CompassQuadrant;
